import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import type { ElementLabelConfig } from '../../elements/label';
import { StyleSectionComponent } from './style-section.component';
import type { StyleSectionDescriptor } from './style-property-schema.model';

/**
 * Top-level multi-section style editor.
 * Handles subpart path navigation for nested style structures (graphpad FeatureStyleNode).
 *
 * For flat structures (geometrypad), styleData is { point: {...}, line: {...}, ... }.
 * For nested structures (graphpad), subpartPath navigates: styleData.subparts[path[0]].subparts[path[1]]...
 */
@Component({
  selector: 'dojo-style-editor',
  standalone: true,
  imports: [StyleSectionComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-1.5">
      @for (section of sections(); track section.name) {
        <dojo-style-section
          [name]="section.name"
          [styleKey]="section.styleKey"
          [styleData]="getSectionStyleData(section)"
          [defaults]="section.defaults ?? {}"
          [showLabel]="section.showLabel === true"
          [showVisible]="section.showVisible === true"
          [visibleValue]="getSectionVisibleValue(section)"
          [labelValue]="getSectionLabelValue(section)"
          (styleChange)="onSectionStyleChange(section, $event.key, $event.value)"
          (labelChange)="onSectionLabelChange(section, $event)"
          (visibleChange)="onSectionVisibleChange(section, $event)"
        />
      }
    </div>
  `,
})
export class StyleEditorComponent {
  readonly sections = input.required<readonly StyleSectionDescriptor[]>();
  readonly styleData = input<Record<string, unknown>>({});
  /** Default label states per subpart name, used as fallback when no explicit label config exists. */
  readonly labelDefaults = input<Record<string, boolean> | null>(null);
  /** Default visible states per subpart name, used as fallback when no explicit visible exists. */
  readonly visibleDefaults = input<Record<string, boolean> | null>(null);

  readonly styleDataChange = output<Record<string, unknown>>();

  getSectionStyleData(section: StyleSectionDescriptor): Record<string, unknown> {
    const node = this.resolveNode(section);
    return (node[section.styleKey] as Record<string, unknown>) ?? {};
  }

  getSectionLabelValue(section: StyleSectionDescriptor): ElementLabelConfig | boolean | undefined {
    const node = this.resolveNode(section);
    const style = (node[section.styleKey] as Record<string, unknown>) ?? {};
    const explicit = style['label'] as ElementLabelConfig | boolean | undefined;
    if (explicit !== undefined) {
      return explicit;
    }

    // Fall back to computed defaults from global geometry config
    const defaults = this.labelDefaults();
    if (defaults && section.subpartPath?.length) {
      const subpartName = section.subpartPath[section.subpartPath.length - 1];
      const defaultValue = defaults[subpartName];
      if (defaultValue !== undefined) {
        return defaultValue;
      }
    }
    return undefined;
  }

  getSectionVisibleValue(section: StyleSectionDescriptor): boolean | undefined {
    const node = this.resolveNode(section);
    if (section.subpartPath && section.subpartPath.length > 0) {
      const explicit = node['visible'] as boolean | undefined;
      if (explicit !== undefined) {
        return explicit;
      }

      // Fall back to computed visible defaults (scope-based angle visibility, etc.)
      const defaults = this.visibleDefaults();
      if (defaults) {
        const subpartName = section.subpartPath[section.subpartPath.length - 1];
        const defaultValue = defaults[subpartName];
        if (defaultValue !== undefined) {
          return defaultValue;
        }
      }
      return true;
    }
    // Non-subpart sections: read from the style key (e.g. point.visible, line.visible)
    const style = (node[section.styleKey] as Record<string, unknown>) ?? {};
    return style['visible'] as boolean | undefined;
  }

  onSectionStyleChange(section: StyleSectionDescriptor, property: string, value: unknown): void {
    const root = { ...this.styleData() };
    const cleanValue = value === '' ? undefined : value;

    if (!section.subpartPath || section.subpartPath.length === 0) {
      const currentStyle = (root[section.styleKey] as Record<string, unknown>) ?? {};
      root[section.styleKey] = { ...currentStyle, [property]: cleanValue };
    } else {
      this.setNestedValue(root, section.subpartPath, section.styleKey, property, cleanValue);
    }

    this.styleDataChange.emit(root);
  }

  onSectionLabelChange(section: StyleSectionDescriptor, labelConfig: ElementLabelConfig | undefined): void {
    const root = { ...this.styleData() };

    // When toggling OFF a label that is ON by default (from global config),
    // write { visible: false } instead of undefined (which would revert to default ON).
    let effectiveConfig: ElementLabelConfig | undefined = labelConfig;
    if (labelConfig === undefined && section.subpartPath?.length) {
      const defaults = this.labelDefaults();
      if (defaults) {
        const subpartName = section.subpartPath[section.subpartPath.length - 1];
        if (defaults[subpartName]) {
          effectiveConfig = { visible: false };
        }
      }
    }

    if (!section.subpartPath || section.subpartPath.length === 0) {
      const currentStyle = (root[section.styleKey] as Record<string, unknown>) ?? {};
      root[section.styleKey] = { ...currentStyle, label: effectiveConfig };
    } else {
      this.setNestedValue(root, section.subpartPath, section.styleKey, 'label', effectiveConfig);
    }

    this.styleDataChange.emit(root);
  }

  onSectionVisibleChange(section: StyleSectionDescriptor, visible: boolean): void {
    const root = { ...this.styleData() };

    if (!section.subpartPath || section.subpartPath.length === 0) {
      // Non-subpart sections: write to the style key (e.g. point.visible, line.visible)
      const currentStyle = (root[section.styleKey] as Record<string, unknown>) ?? {};
      root[section.styleKey] = { ...currentStyle, visible };
    } else {
      // Always write the explicit value so the creator can respect the override
      this.setNestedNodeValue(root, section.subpartPath, 'visible', visible);
    }

    this.styleDataChange.emit(root);
  }

  /** Navigate to the correct node for a given section */
  private resolveNode(section: StyleSectionDescriptor): Record<string, unknown> {
    let node = this.styleData();
    if (section.subpartPath) {
      for (const segment of section.subpartPath) {
        const subparts = (node['subparts'] as Record<string, Record<string, unknown>>) ?? {};
        node = subparts[segment] ?? {};
      }
    }
    return node;
  }

  /**
   * Set a value deep in the subparts tree:
   * root.subparts[path[0]].subparts[path[1]]...[styleKey][property] = value
   */
  private setNestedValue(
    root: Record<string, unknown>,
    path: readonly string[],
    styleKey: string,
    property: string,
    value: unknown,
  ): void {
    let node = root;
    for (const segment of path) {
      if (!node['subparts']) {
        node['subparts'] = {};
      }
      const subparts = { ...(node['subparts'] as Record<string, Record<string, unknown>>) };
      node['subparts'] = subparts;
      if (!subparts[segment]) {
        subparts[segment] = {};
      }
      subparts[segment] = { ...subparts[segment] };
      node = subparts[segment];
    }
    const currentStyle = { ...((node[styleKey] as Record<string, unknown>) ?? {}) };
    currentStyle[property] = value;
    node[styleKey] = currentStyle;
  }

  /**
   * Set a node-level property deep in the subparts tree:
   * root.subparts[path[0]].subparts[path[1]]...[property] = value
   */
  private setNestedNodeValue(
    root: Record<string, unknown>,
    path: readonly string[],
    property: string,
    value: unknown,
  ): void {
    let node = root;
    for (const segment of path) {
      if (!node['subparts']) {
        node['subparts'] = {};
      }
      const subparts = { ...(node['subparts'] as Record<string, Record<string, unknown>>) };
      node['subparts'] = subparts;
      if (!subparts[segment]) {
        subparts[segment] = {};
      }
      subparts[segment] = { ...subparts[segment] };
      node = subparts[segment];
    }
    node[property] = value;
  }
}
